import { Component, inject, Signal, signal } from '@angular/core';
import { disabled, form, FormField, FormRoot, required, submit } from '@angular/forms/signals';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { UiButtonComponent } from '@app/shared/ui/button/ui-button.component';

import { phoneNumber } from '@app/shared/forms/rules';
import { ActiveCompanyService } from '@app/shared/services/active-company.service';
import { UsersDataService } from '@app/shared/services/users-data.service';
import { Company } from '@app/shared/types/models/company/company.model';
import { UpdateUserDetailsRequestBody } from '@app/shared/types/models/user/user-dto.model';
import { User } from '@app/shared/types/models/user/user.model';
import { UiDialogComponent } from '@app/shared/ui/dialog/ui-dialog.component';
import { UiInputComponent } from '@app/shared/ui/input/ui-input.component';
import { TranslatePipe } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

export interface UserEditDialogData {
  userToEdit: User;
}

interface EditUserFormModel {
  email: string;
  firstName: string;
  lastName: string;
  notes: string;
  address: string;
  phoneNumbers: string;
}

@Component({
  selector: 'proffeo-edit-user-dialog',
  imports: [
    TranslatePipe,
    UiDialogComponent,
    FormField,
    FormRoot,
    MatDialogModule,
    UiButtonComponent,
    UiInputComponent
  ],
  templateUrl: './edit-user-dialog.component.html'
})
export class EditUserDialogComponent {
  private readonly userService: UsersDataService = inject(UsersDataService);
  private readonly activeCompanyService: ActiveCompanyService = inject(ActiveCompanyService);
  private readonly dialogRef: MatDialogRef<EditUserDialogComponent> = inject(MatDialogRef<EditUserDialogComponent>);
  private readonly dialogData: UserEditDialogData = inject(MAT_DIALOG_DATA);

  protected readonly model = signal<EditUserFormModel>(this.toFormModel(this.dialogData.userToEdit));
  protected readonly form = form(this.model, path => {
    disabled(path.email);
    required(path.firstName);
    required(path.lastName);
    phoneNumber(path.phoneNumbers);
  });
  protected activeCompany: Signal<Company | undefined> = this.activeCompanyService.activeCompany;

  protected saveUser(): void {
    const companyId = this.activeCompany()?.id;
    if (!companyId) return;

    void submit(this.form, {
      action: async () => {
        const request = this.toRequest();
        try {
          await firstValueFrom(this.userService.updateUserDetails(companyId, this.dialogData.userToEdit.id, request));
        } finally {
          this.dialogRef.close(request);
        }
      }
    });
  }

  private toFormModel(user: User): EditUserFormModel {
    return {
      email: user.email ?? '',
      firstName: user.firstName ?? '',
      lastName: user.lastName ?? '',
      notes: user.notes ?? '',
      address: user.address ?? '',
      phoneNumbers: user.phoneNumbers ?? ''
    };
  }

  /** Optional fields the user never had and left empty stay absent from the request (as before). */
  private toRequest(): UpdateUserDetailsRequestBody {
    const user = this.dialogData.userToEdit;
    const value = this.model();
    const optional = (current: string, original: string | undefined): string | undefined =>
      current === '' && original == null ? undefined : current;

    return {
      firstName: value.firstName,
      lastName: value.lastName,
      notes: optional(value.notes, user.notes),
      address: optional(value.address, user.address),
      phoneNumbers: optional(value.phoneNumbers, user.phoneNumbers)
    };
  }
}
